import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, EyeOff, Flag, ExternalLink } from "lucide-react";
import { useLanguage, useT } from "../../lib/LanguageContext";
import usePagedList from "../../hooks/usePagedList";
import LoadFailure from "../../components/ui/LoadFailure";
import Pager from "../../components/ui/Pager";
import StarRating from "../../reviews/components/StarRating";
import { REVIEW_PATHS } from "../../reviews/contract";
import { reviewErrorText } from "../../reviews/errorText";
import { fmtAvg, fmtDate } from "../../reviews/format";
import { REVIEWS } from "../../constants/testIds/reviews";
import PracticeReviews from "./PracticeReviews";

/** `any` sends no filter at all — the tri-state a boolean checkbox cannot express. */
const TRISTATE = ["", "1", "0"];
const TRISTATE_KEYS = { "": "srev.filter.any", 1: "srev.filter.yes", 0: "srev.filter.no" };

/** `https://lunaria.solunary.health` → `lunaria.solunary.health`; "" when there is none. */
const hostOf = (url) => {
  if (!url) return "";
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
};

/**
 * The cross-tenant moderation console, GROUPED BY PRACTICE.
 *
 * It opens on one header per practice (`GET /super-admin/reviews/practices`), each stating
 * whose reviews these are, what visitors see (the public average over visible reviews) and
 * what awaits a decision. A practice's reviews are read only when its section is opened,
 * from the one review list filtered by `tenant_id` — paged per practice, so a practice's
 * reviews never break across a page boundary the way a flat list cut into groups would.
 *
 * ORDER IS THE SERVER'S: practices with an open flag first. A section with an open flag
 * starts OPEN, because that is the work; every other section starts closed, so a platform
 * of two hundred practices is still a readable page. That default is taken once per
 * practice and then kept, so neither the operator's own toggling nor a re-read that no
 * longer shows the flag (because the operator just resolved it) folds a section.
 *
 * THE FILTERS APPLY TWICE, ON PURPOSE. `hidden`/`flagged` decide which practices appear
 * (those with at least one matching review) AND which reviews an opened section lists; the
 * header's counts stay the practice's totals, with `matching_count` saying how many match.
 *
 * THE SEARCH is a contains match on the practice's name, slug or id. It is operator-only
 * (`tenants.read`, the permission that already lists every practice on the Tenants
 * screen), so it discloses nothing that permission does not.
 *
 * A HIDE OR UNHIDE RE-READS BOTH LEVELS: the section's rows, and these headers — the
 * practice's hidden count and public average moved on the server in the same request.
 */
export default function ModerationTable() {
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();

  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [hidden, setHidden] = useState("");
  const [flagged, setFlagged] = useState("");
  const [openState, setOpenState] = useState({});

  const filters = { ...(hidden ? { hidden } : {}), ...(flagged ? { flagged } : {}) };
  const practices = usePagedList(REVIEW_PATHS.adminPractices, {
    ...(q ? { q } : {}),
    ...filters,
  });
  const filtering = Boolean(hidden || flagged);

  // The default (open when a flag awaits a decision) is decided ONCE, the first time a
  // practice appears, and then pinned: resolving the operator's own flag must not fold the
  // section they are working in, which is what re-deriving it from each re-read would do.
  useEffect(() => {
    const unseen = practices.rows.filter((p) => !(p.tenant_id in openState));
    if (unseen.length === 0) return;
    setOpenState((s) => {
      const next = { ...s };
      unseen.forEach((p) => { if (!(p.tenant_id in next)) next[p.tenant_id] = p.open_flag_count > 0; });
      return next;
    });
  }, [practices.rows, openState]);

  const isOpen = (p) => (p.tenant_id in openState ? openState[p.tenant_id] : p.open_flag_count > 0);
  const toggle = (p) => setOpenState((s) => ({ ...s, [p.tenant_id]: !isOpen(p) }));

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="sa-reviews-table">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-slate-200">
        <h2 className="text-xl text-slate-800">{t("srev.list.title")}</h2>
        <div className="flex gap-2 flex-wrap">
          <form
            role="search"
            onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }}
            className="flex gap-2"
            data-testid="sa-reviews-search-form"
          >
            <input
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              maxLength={100}
              aria-label={t("srev.filter.practice")}
              placeholder={t("srev.filter.practicePlaceholder")}
              data-testid={REVIEWS.saFilterTenant}
              className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm min-w-[14rem]"
            />
            <button
              type="submit"
              data-testid="sa-reviews-search-submit"
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-200"
            >
              {t("srev.filter.search")}
            </button>
          </form>
          <select
            value={hidden}
            onChange={(e) => setHidden(e.target.value)}
            aria-label={t("srev.filter.hidden")}
            data-testid={REVIEWS.saFilterHidden}
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          >
            {TRISTATE.map((v) => (
              <option key={v || "any"} value={v}>
                {`${t("srev.filter.hidden")} — ${t(TRISTATE_KEYS[v || ""])}`}
              </option>
            ))}
          </select>
          <select
            value={flagged}
            onChange={(e) => setFlagged(e.target.value)}
            aria-label={t("srev.filter.flagged")}
            data-testid={REVIEWS.saFilterFlagged}
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          >
            {TRISTATE.map((v) => (
              <option key={v || "any"} value={v}>
                {`${t("srev.filter.flagged")} — ${t(TRISTATE_KEYS[v || ""])}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {practices.loadError ? (
        <div className="p-5">
          <LoadFailure
            message={reviewErrorText(practices.loadError, { t, tv, prefix: "srev", fallback: tv("errors.loadFailed") })}
            onRetry={practices.reload}
            retryLabel={tv("actions.retry")}
            testId={REVIEWS.saLoadError}
            retryTestId="sa-reviews-retry"
          />
        </div>
      ) : practices.loaded && practices.rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm" data-testid={REVIEWS.saEmpty}>
          {t(q ? "srev.list.emptySearch" : "srev.list.empty")}
        </div>
      ) : (
        <>
          <ul className="m-0 p-0 divide-y divide-slate-200">
            {practices.rows.map((p) => (
              <PracticeSection
                key={p.tenant_id}
                practice={p}
                open={isOpen(p)}
                onToggle={() => toggle(p)}
                filters={filters}
                filtering={filtering}
                onChanged={practices.reload}
                t={t}
                lang={lang}
              />
            ))}
          </ul>
          <Pager
            page={practices.page}
            pageSize={practices.pageSize}
            total={practices.total}
            shown={practices.rows.length}
            onPage={practices.setPage}
            infoText={(vars) => t("srev.practice.pageInfo", vars)}
            prevLabel={t("srev.page.prev")}
            nextLabel={t("srev.page.next")}
            testId="sa-reviews-page"
            tone="slate"
          />
        </>
      )}
    </section>
  );
}

/** One practice: a header that names it and states its numbers, and — opened — its reviews. */
function PracticeSection({ practice: p, open, onToggle, filters, filtering, onChanged, t, lang }) {
  const bodyId = `sa-reviews-practice-body-${p.tenant_id}`;
  const host = hostOf(p.site_url);
  const deleted = !p.status;

  return (
    <li className="list-none" data-testid={REVIEWS.saPractice(p.tenant_id)}>
      <div className="flex items-start gap-3 px-5 py-4 bg-slate-50/60">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          data-testid={REVIEWS.saPracticeToggle(p.tenant_id)}
          className="flex items-start gap-3 min-w-0 flex-1 text-left"
        >
          {open
            ? <ChevronDown className="w-4 h-4 mt-1 shrink-0 text-slate-500" aria-hidden="true" />
            : <ChevronRight className="w-4 h-4 mt-1 shrink-0 text-slate-500" aria-hidden="true" />}
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-base font-semibold text-slate-900 break-words">{p.name}</span>
              {host && <span className="text-xs font-mono text-slate-500 break-all">{host}</span>}
              {deleted && (
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 text-slate-600">
                  {t("srev.practice.deleted")}
                </span>
              )}
            </span>
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-600">
              {p.avg_rating != null ? (
                <span className="inline-flex items-center gap-1.5">
                  <StarRating
                    value={p.avg_rating}
                    readOnly
                    variant="light"
                    size="sm"
                    label={t("srev.row.aria", { avg: fmtAvg(p.avg_rating) })}
                  />
                  <span className="tabular-nums">
                    {t("srev.practice.public", { avg: fmtAvg(p.avg_rating), n: p.visible_count })}
                  </span>
                </span>
              ) : (
                <span>{t("srev.practice.noPublic")}</span>
              )}
              <span className="tabular-nums">{t("srev.practice.total", { n: p.review_count })}</span>
              {p.open_flag_count > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200 tabular-nums">
                  <Flag className="w-3 h-3" aria-hidden="true" /> {t("srev.practice.openFlags", { n: p.open_flag_count })}
                </span>
              )}
              {p.hidden_count > 0 && (
                <span className="inline-flex items-center gap-1 tabular-nums">
                  <EyeOff className="w-3 h-3" aria-hidden="true" /> {t("srev.practice.hidden", { n: p.hidden_count })}
                </span>
              )}
              {filtering && (
                <span className="tabular-nums text-slate-500">
                  {t("srev.practice.matching", { matching: p.matching_count, total: p.review_count })}
                </span>
              )}
              {p.latest_review_at && (
                <span className="tabular-nums text-slate-500">
                  {t("srev.practice.latest", { date: fmtDate(p.latest_review_at, lang) })}
                </span>
              )}
            </span>
          </span>
        </button>
        {p.marketplace_url && (
          <a
            href={p.marketplace_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={REVIEWS.saPracticeMarketplace(p.tenant_id)}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 bg-white"
          >
            <ExternalLink className="w-3 h-3" aria-hidden="true" /> {t("srev.practice.marketplace")}
          </a>
        )}
      </div>
      {/* The region always exists so `aria-controls` never points at nothing; its reviews
          are read only while it is open. */}
      <div id={bodyId} hidden={!open}>
        {open && <PracticeReviews tenantId={p.tenant_id} filters={filters} onChanged={onChanged} />}
      </div>
    </li>
  );
}
